import { useSyncExternalStore } from "react";

/**
 * The viewer's look preferences on this device (a convenience, so `localStorage`): mouse and
 * stick sensitivity, inverted vertical look, the first-person head bob. Reads never throw.
 */
export interface LookPrefs {
  /** Multiplier on the look speed, 0.3..3. */
  sensitivity: number;
  invertY: boolean;
  headBob: boolean;
}

export const DEFAULT_LOOK: LookPrefs = { sensitivity: 1, invertY: false, headBob: true };
const KEY = "uz.world.look";

function read(): LookPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<LookPrefs> | null;
    if (!raw || typeof raw !== "object") return DEFAULT_LOOK;
    const s = Number(raw.sensitivity);
    return {
      sensitivity: Number.isFinite(s) ? Math.max(0.3, Math.min(3, s)) : 1,
      invertY: raw.invertY === true,
      headBob: raw.headBob !== false,
    };
  } catch {
    return DEFAULT_LOOK;
  }
}

let state: LookPrefs = read();
const listeners = new Set<() => void>();

export const getLookPrefs = (): LookPrefs => state;
export function setLookPrefs(patch: Partial<LookPrefs>): void {
  state = { ...state, ...patch };
  state.sensitivity = Math.max(0.3, Math.min(3, state.sensitivity));
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private mode: kept for this session only.
  }
  for (const l of listeners) l();
}
export function useLookPrefs(): LookPrefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getLookPrefs,
    getLookPrefs,
  );
}
